import { InformationContainer } from "@components/containers/Containers"
import SectionTitle from "@components/sectionTitle/SectionTitle"
import { SectionTitleSize, SectionTitleType } from "@components/sectionTitle/types"
import { PartialMetadataObject } from "@pages/DatabasePage/plantMetadata/PlantMetadata"
import { t } from "i18next"
import { useEffect, useRef, useState } from "react"

const VesselInfoFullTextLine = ({ data }: { data: [string, string[]] }) => {
	return (
		<>
			<p className={`col-span-8 text-justify font-normal`}>{data[1]}</p>
		</>
	)
}

const VesselInfoClassDefinitionLine = ({ data }: { data: [string, string[]] }) => {
	return (
		<>
			<div className={`text-dipai-secondary-901 col-span-8 font-semibold`}>{data[0]}</div>
			<div className={`text-dipai-secondary-901/90 col-span-8 flex justify-end text-justify`}>
				{Array.isArray(data[1]) === true ? (
					<div className={`flex flex-col`}>
						{data[1].map((entry, index) => (
							<div
								className={`flex justify-end pt-1`}
								key={index}
							>
								{entry}
							</div>
						))}
					</div>
				) : null}
			</div>
		</>
	)
}

const VesselInfoNormalLine = ({ data }: { data: [string, string[]] }) => {
	const refLabel = useRef<HTMLParagraphElement>(null)
	const [ref, setRef] = useState<(HTMLParagraphElement | null)[]>([])
	const [refLabelSpan, setRefLabelSpan] = useState<string>("col-span-4")
	const [refSpan, setRefSpan] = useState<string[]>([])
	const [refPadding, setRefPadding] = useState<string[]>([])

	useEffect(() => {
		if (refLabel.current === null) {
			return
		}
		if (
			(refLabel.current?.clientHeight !== undefined && refLabel.current?.clientHeight > 30) ||
			(refLabel.current?.parentNode as HTMLElement)?.className.includes("col-span-8") === true
		) {
			setRefLabelSpan("col-span-8")
		}
	}, [refLabel.current])

	useEffect(() => {
		if (ref.length === 0) {
			return
		}
		let tempRefSpan = refSpan
		let tempRefPadding = refPadding

		for (const [idx, r] of ref.entries()) {
			if (tempRefSpan[idx] === undefined) {
				tempRefSpan.push("col-span-4")
			}
			if (
				(r?.clientHeight !== undefined && r?.clientHeight! > 30) ||
				(r?.parentNode as HTMLElement)?.className.includes("col-span-8") === true
			) {
				tempRefSpan = tempRefSpan.map((span, index) =>
					index === idx ? "col-span-8" : span
				)
			}

			if (refPadding[idx] === undefined) {
				tempRefPadding.push("")
			}
			if (
				idx > 0 ||
				(refLabel.current?.parentNode as HTMLElement)?.className.includes("col-span-8") ===
					true ||
				(r?.parentNode as HTMLElement)?.className.includes("col-span-8") === true
			) {
				tempRefPadding = tempRefPadding.map((padding, index) =>
					index === idx ? "pt-[7px]" : padding
				)
			}
		}
		setRefSpan(tempRefSpan)
		setRefPadding(tempRefPadding)
	}, [ref.length])

	return (
		<>
			<div className={`${refLabelSpan} text-dipai-secondary-901 font-semibold`}>
				<p ref={refLabel}>{data[0]}</p>
			</div>
			{Array.isArray(data[1]) === true
				? data[1].map((entry, index) => (
						<div
							className={`${refSpan[index] ?? "col-span-4"} text-dipai-secondary-901/90 flex justify-end ${
								refPadding[index] ?? ""
							}`}
							key={index}
							style={{ gridColumnEnd: index === 0 ? "auto" : -1 }}
						>
							<p
								ref={(newRef) => {
									setRef((prevRef) => {
										if (prevRef.length < index + 1) {
											prevRef.push(newRef)
										}
										return prevRef
									})
								}}
								id={`${entry}`}
							>
								{entry}
							</p>
						</div>
					))
				: null}
		</>
	)
}

export default function VesselSpecSheetContent({
	vesselSpecSheetData,
}: {
	vesselSpecSheetData: PartialMetadataObject | null
}) {
	const [vesselInfoContainerRef, setVesselInfoContainerRef] = useState<HTMLDivElement | null>(
		null
	)
	const [vesselInfoContainerRefHeight, setVesselInfoContainerRefHeight] = useState<number>(0)

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			const entries_0 = entries[0]
			if (entries_0 === undefined) {
				return
			}
			const newHeight = entries_0.target.clientHeight
			setVesselInfoContainerRefHeight(newHeight)
		})

		if (vesselInfoContainerRef !== null) {
			resizeObserver.observe(vesselInfoContainerRef)
		}

		return () => {
			if (vesselInfoContainerRef !== null) {
				resizeObserver.unobserve(vesselInfoContainerRef)
			}
		}
	}, [vesselInfoContainerRef])

	return vesselSpecSheetData === null ? null : (
		<>
			<SectionTitle
				title={t("VESSEL SPEC SHEET").toUpperCase()}
				type={SectionTitleType.vessel}
				size={SectionTitleSize.medium}
			/>
			<InformationContainer>
				<>
					<div
						className={`text-small grid w-[calc(100%-1rem)] max-w-[570px] grid-cols-2 gap-x-2`}
					>
						{vesselSpecSheetData["Class definition"] !== null ? (
							<div
								className={`col-span-2 grid h-auto w-full grid-cols-8 border-b-1 py-1 pr-0`}
							>
								<VesselInfoClassDefinitionLine
									data={[
										"Class definition",
										[`${vesselSpecSheetData["Class definition"] ?? "--"}`],
									]}
								/>
							</div>
						) : null}
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Vessel built",
									[`${vesselSpecSheetData["Vessel built"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Vessel design",
									[`${vesselSpecSheetData["Vessel design"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Length o.a.",
									[`${vesselSpecSheetData["Length o.a."] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Length b.p.",
									[`${vesselSpecSheetData["Length b.p."] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Breadth mld",
									[`${vesselSpecSheetData["Breadth mld"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={["Bext", [`${vesselSpecSheetData["Bext"] ?? "--"}`]]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Design draft",
									[`${vesselSpecSheetData["Design draft"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Summer draft",
									[`${vesselSpecSheetData["Summer draft"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Scantling draft",
									[`${vesselSpecSheetData["Scantling draft"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={["Depth", [`${vesselSpecSheetData["Depth"] ?? "--"}`]]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-0 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Service speed",
									[`${vesselSpecSheetData["Service speed"] ?? "--"}`],
								]}
							/>
						</div>
						<div className={`grid h-auto w-full grid-cols-8 border-b-0 pt-1 pr-0 pb-1`}>
							<VesselInfoNormalLine
								data={[
									"Accommodation",
									[`${vesselSpecSheetData["Accommodation"] ?? "--"}`],
								]}
							/>
						</div>
					</div>
					{vesselSpecSheetData["additionalProps"] !== null &&
					vesselSpecSheetData["additionalProps"] !== undefined ? (
						<>
							<p
								className={`text-small bg-dipai-secondary-800 mt-2 -ml-2 border-y-0 pt-1 pb-1 pl-2 font-semibold text-white`}
							>
								Additional data
							</p>
							<div
								className={`text-small flex w-full max-w-[566px] flex-col flex-wrap pt-1`}
								style={{
									height: Math.ceil(vesselInfoContainerRefHeight / 2) * 1 + 70,
								}}
							>
								{Object.entries(
									vesselSpecSheetData["additionalProps"] as {
										[vesselProp: string]: string[]
									}
								).map((data) => (
									<div
										key={data[0]}
										className={`grid h-auto w-[calc(50%-2px-8px+2px)] grid-cols-8 border-b-1 py-1 pr-0`}
									>
										{data[0].includes("*") === true ? (
											<VesselInfoFullTextLine data={data} />
										) : data[0] === "Class definition" ? (
											<VesselInfoClassDefinitionLine data={data} />
										) : (
											<VesselInfoNormalLine data={data} />
										)}
									</div>
								))}
							</div>
							{/* USE THIS DIV TO FIND THE RENDERED LIST SIZE TO USE AS REFERENCE HEIGHT
                        AND POSITION IT OUTSIDE OF VIEW */}
							<div
								ref={(newRef) => setVesselInfoContainerRef(newRef)}
								className={`text-small absolute flex w-full max-w-[557px] flex-col flex-wrap pt-1 opacity-0`}
							>
								{Object.entries(
									vesselSpecSheetData["additionalProps"] as {
										[vesselProp: string]: string[]
									}
								).map((data) => (
									<div
										key={data[0]}
										className={`grid h-auto w-[calc(50%-2px-8px+2px)] grid-cols-8 border-b-1 py-1 pr-0`}
									>
										{data[0].includes("*") === true ? (
											<VesselInfoFullTextLine data={data} />
										) : data[0] === "Class definition" ? (
											<VesselInfoClassDefinitionLine data={data} />
										) : (
											<VesselInfoNormalLine data={data} />
										)}
									</div>
								))}
							</div>
						</>
					) : null}
				</>
			</InformationContainer>
		</>
	)
}

export function VesselSpecSheetContentSmall({
	vesselSpecSheetData,
}: {
	vesselSpecSheetData: PartialMetadataObject | null
}) {
	const [vesselInfoContainerRef, setVesselInfoContainerRef] = useState<HTMLDivElement | null>(
		null
	)
	const [vesselInfoContainerRefHeight, setVesselInfoContainerRefHeight] = useState<number>(0)

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			const entries_0 = entries[0]
			if (entries_0 === undefined) {
				return
			}
			const newHeight = entries_0.target.clientHeight
			setVesselInfoContainerRefHeight(newHeight)
		})

		if (vesselInfoContainerRef !== null) {
			resizeObserver.observe(vesselInfoContainerRef)
		}

		return () => {
			if (vesselInfoContainerRef !== null) {
				resizeObserver.unobserve(vesselInfoContainerRef)
			}
		}
	}, [vesselInfoContainerRef])

	return vesselSpecSheetData === null ? null : (
		<>
			<div className="bg-dipai-secondary-901 flex h-7 w-full items-center justify-between px-2">
				<p className="text-menu font-medium text-white">{t("VESSEL SPEC SHEET")}</p>
			</div>
			<div className={`p-2 pr-0 pb-0`}>
				<div
					className={`text-tiny grid w-[calc(100%-0.5rem)] max-w-[400px] grid-cols-2 gap-x-2`}
				>
					{vesselSpecSheetData["Class definition"] !== null ? (
						<div
							className={`col-span-2 grid h-auto w-full grid-cols-8 border-b-1 py-1 pr-0`}
						>
							<VesselInfoClassDefinitionLine
								data={[
									"Class definition",
									[`${vesselSpecSheetData["Class definition"] ?? "--"}`],
								]}
							/>
						</div>
					) : null}
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Vessel built",
								[`${vesselSpecSheetData["Vessel built"] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={["Vessel design", vesselSpecSheetData["Vessel design"] ?? ["--"]]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Length o.a.",
								[`${vesselSpecSheetData["Length o.a."] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Length b.p.",
								[`${vesselSpecSheetData["Length b.p."] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Breadth mld",
								[`${vesselSpecSheetData["Breadth mld"] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={["Bext", [`${vesselSpecSheetData["Bext"] ?? "--"}`]]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Design draft",
								[`${vesselSpecSheetData["Design draft"] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Summer draft",
								[`${vesselSpecSheetData["Summer draft"] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Scantling draft",
								[`${vesselSpecSheetData["Scantling draft"] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-1 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={["Depth", [`${vesselSpecSheetData["Depth"] ?? "--"}`]]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-0 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Service speed",
								[`${vesselSpecSheetData["Service speed"] ?? "--"}`],
							]}
						/>
					</div>
					<div className={`grid h-auto w-full grid-cols-8 border-b-0 pt-1 pr-0 pb-1`}>
						<VesselInfoNormalLine
							data={[
								"Accommodation",
								[`${vesselSpecSheetData["Accommodation"] ?? "--"}`],
							]}
						/>
					</div>
				</div>
				{vesselSpecSheetData["additionalProps"] !== null &&
				vesselSpecSheetData["additionalProps"] !== undefined ? (
					<>
						<p
							className={`text-tiny bg-dipai-secondary-800 mt-2 -ml-1 border-y-0 pt-1 pb-1 pl-1 font-semibold text-white`}
						>
							Additional data
						</p>
						{vesselInfoContainerRefHeight !== 0 ? (
							<div
								className={`text-tiny flex w-[calc(100%-0px)] max-w-[400px] flex-col flex-wrap pt-1`}
								style={{
									height: Math.ceil(vesselInfoContainerRefHeight / 2) * 1 + 70,
									// maxHeight: Math.ceil(vesselInfoContainerRefHeight / 2) * 1 + 70,
								}}
							>
								{Object.entries(
									vesselSpecSheetData["additionalProps"] as {
										[vesselProp: string]: string[]
									}
								).map((data) => (
									<div
										key={data[0]}
										className={`grid h-auto w-[calc(50%-2px-8px+2px)] grid-cols-8 border-b-1 pt-1 pr-0 pb-[2px]`}
									>
										{data[0].includes("*") === true ? (
											<VesselInfoFullTextLine data={data} />
										) : data[0] === "Class definition" ? (
											<VesselInfoClassDefinitionLine data={data} />
										) : (
											<VesselInfoNormalLine data={data} />
										)}
									</div>
								))}
							</div>
						) : null}
						{/* USE THIS DIV TO FIND THE RENDERED LIST SIZE TO USE AS REFERENCE HEIGHT
                        AND POSITION IT OUTSIDE OF VIEW */}
						<div
							ref={(newRef) => setVesselInfoContainerRef(newRef)}
							className={`text-tiny absolute top-[200vh] z-0 flex w-[calc(100%-0px)] max-w-[363px] flex-col flex-wrap pt-1 opacity-0`}
						>
							{Object.entries(
								vesselSpecSheetData["additionalProps"] as {
									[vesselProp: string]: string[]
								}
							).map((data) => (
								<div
									key={data[0]}
									className={`grid h-auto w-[calc(50%-2px-8px+2px)] grid-cols-8 border-b-1 pt-1 pr-0 pb-[2px]`}
								>
									{data[0].includes("*") === true ? (
										<VesselInfoFullTextLine data={data} />
									) : data[0] === "Class definition" ? (
										<VesselInfoClassDefinitionLine data={data} />
									) : (
										<VesselInfoNormalLine data={data} />
									)}
								</div>
							))}
						</div>
					</>
				) : null}
			</div>
		</>
	)
}
